import axiosInstance, { ApiKeys, getApiUrl, logError } from '@cp/src/utils/ApiHelper';
import { Dispatch } from '@reduxjs/toolkit';
import {
  setActivityLogs,
  setActivityLogsLoading,
  setAnomalyActionLoading,
  setAnomalyClosedFilterSchema,
  setAnomalyClosedTickets,
  setAnomalyOpenFilterSchema,
  setAnomalyOpenTickets,
  setAnomalyTicketsLoading,
  setRcaReasons,
  setSingleAnomalyDetails,
  setSingleAnomalyDetailsLoading
} from '../reducers/AnomalyTrackerSlice';
import { AnomalyStatus, AnomalyStatusType, IUpdateAnomalyPayload } from '../types';
import { GenericObject } from '@cp/src/types/CommonConstans';
import { API_STATUS_CODE } from '@cp/src/utils/ApiHelperUtils';

export const getAnomalyTickets =
  (anomalyStatus: string, payload: GenericObject, showLoader = true) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.GET_ANOMALY_DETAILS);
    const status =
      anomalyStatus === AnomalyStatus.OPEN ? AnomalyStatusType.OPEN : AnomalyStatusType.RESOLVED;
    if (showLoader) dispatch(setAnomalyTicketsLoading(true));
    axiosInstance
      .get(url, { params: { ...payload, status } })
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          const data = response?.data;
          if (anomalyStatus === AnomalyStatus.OPEN) {
            dispatch(setAnomalyOpenTickets(data));
            return;
          }
          dispatch(setAnomalyClosedTickets(data));
        }
      })
      .catch(err => {
        logError(err);
      })
      .finally(() => {
        dispatch(setAnomalyTicketsLoading(false));
      });
  };

export const fetchDynamicFilters = (anomalyType: AnomalyStatusType) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_ANOMALY_TRACKER_FILTERS);
  axiosInstance
    .get(url, { params: { status: anomalyType } })
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        if (anomalyType === AnomalyStatusType.OPEN) {
          dispatch(setAnomalyOpenFilterSchema(response?.data?.filerModelMap));
          return;
        }
        dispatch(setAnomalyClosedFilterSchema(response?.data?.filerModelMap));
      }
    })
    .catch(err => {
      logError(err);
    });
};

export const getSingleAnomalyTicket = (anomalyReferenceId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_ANOMALY_SINGLE_TICKET, {
    anomalyReferenceId
  });
  dispatch(setSingleAnomalyDetailsLoading(true));
  axiosInstance
    .get(url)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        dispatch(setSingleAnomalyDetails(response?.data));
      }
    })
    .catch(err => {
      logError(err);
    })
    .finally(() => dispatch(setSingleAnomalyDetailsLoading(false)));
};

export const updateAnomaly =
  (anomalyReferenceId: string, payload: IUpdateAnomalyPayload, callbackFn: () => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.UPDATE_ANOMALY_TICKET, { anomalyReferenceId });
    dispatch(setAnomalyActionLoading(true));
    axiosInstance
      .put(url, payload)
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          callbackFn?.();
        }
      })
      .catch(err => {
        logError(err);
      })
      .finally(() => dispatch(setAnomalyActionLoading(false)));
  };

export const resolveAnomaly =
  (anomalyReferenceId: string, payload: IUpdateAnomalyPayload, callbackFn: () => void) =>
  (dispatch: Dispatch) => {
    const url = getApiUrl(ApiKeys.RESOLVE_ANOMALY_TICKET, { anomalyReferenceId });
    dispatch(setAnomalyActionLoading(true));
    axiosInstance
      .put(url, payload)
      .then(response => {
        if (response.status === API_STATUS_CODE.OK) {
          callbackFn?.();
        }
      })
      .catch(err => {
        logError(err);
      })
      .finally(() => dispatch(setAnomalyActionLoading(false)));
  };

export const getRcaQuestionTree = (anomalyReferenceId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_ANOMALY_RCA_QUESTION, { anomalyReferenceId });
  axiosInstance
    .get(url)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        dispatch(setRcaReasons(response?.data));
      }
    })
    .catch(err => {
      logError(err);
    });
};

export const getActivityLogs = (anomalyReferenceId: string) => (dispatch: Dispatch) => {
  const url = getApiUrl(ApiKeys.GET_ANOMALY_ACTIVITY_LOG, { anomalyReferenceId });
  dispatch(setActivityLogsLoading(true));
  axiosInstance
    .get(url)
    .then(response => {
      if (response.status === API_STATUS_CODE.OK) {
        dispatch(setActivityLogs(response?.data));
      }
    })
    .catch(err => {
      logError(err);
    })
    .finally(() => dispatch(setActivityLogsLoading(false)));
};
